import React from "react";
import { LandingPageLayout } from "../shared/layout";
import { Badge } from "@/components/ui/badge";

function LegalLayout({ badge, title, updated, children }) {
  return (
    <LandingPageLayout>
      <div className="py-16 bg-white dark:bg-gray-900 min-h-screen">
        <div className="container mx-auto px-4 max-w-3xl">
          <Badge variant="outline" className="mb-4">{badge}</Badge>
          <h1 className="text-3xl font-extrabold tracking-tight text-gray-900 dark:text-white mb-2">
            {title}
          </h1>
          <p className="text-sm text-gray-400 mb-8">Last updated: {updated}</p>
          <div className="prose dark:prose-invert max-w-none space-y-6 text-gray-700 dark:text-gray-300">
            {children}
          </div>
        </div>
      </div>
    </LandingPageLayout>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">{title}</h2>
      <div className="space-y-2 text-sm leading-relaxed">{children}</div>
    </section>
  );
}

const UPDATED = "August 1, 2026";

export function AiDisclaimerScreen() {
  return (
    <LegalLayout badge="AI Disclaimer" title="How Pairvio's AI Works" updated={UPDATED}>
      <Section title="You are talking to an AI">
        <p>
          Every conversation in Pairvio is with an artificial intelligence,
          not a human. The AI plays fictional practice characters in roleplay
          scenarios and gives communication-skill feedback. It has no feelings,
          no memory of you as a person outside your account data, and no
          relationship with you.
        </p>
      </Section>
      <Section title="A coach, not a companion">
        <p>
          Pairvio is designed for practicing conversations — first dates,
          setting boundaries, difficult talks. It is intentionally NOT an AI
          girlfriend, boyfriend, or emotional companion. The AI will not tell
          you it loves you or misses you, and it will not simulate being your
          real partner or any real person.
        </p>
      </Section>
      <Section title="Not therapy or medical advice">
        <p>
          Pairvio is a skills-practice tool. It is not therapy, counseling,
          psychological treatment, or medical advice, and it does not diagnose
          any condition. If you are struggling with your mental health, please
          talk to a licensed professional.
        </p>
      </Section>
      <Section title="If you are in crisis">
        <p>
          If you are thinking about harming yourself or others, stop using the
          app and seek help immediately. In the US, call or text 988 (Suicide
          &amp; Crisis Lifeline). Elsewhere, contact your local emergency
          services.
        </p>
      </Section>
      <Section title="AI limitations">
        <p>
          AI responses can be wrong, generic, or miss important context about
          your life. Treat feedback as practice input, not as instructions.
          You are always responsible for your real-world decisions and
          relationships.
        </p>
      </Section>
    </LegalLayout>
  );
}

export function TermsScreen() {
  return (
    <LegalLayout badge="Legal" title="Terms of Service" updated={UPDATED}>
      <Section title="1. The service">
        <p>
          Pairvio provides AI-powered roleplay scenarios and feedback for
          practicing relationship communication skills. By creating an account
          you agree to these terms.
        </p>
      </Section>
      <Section title="2. Eligibility — 18+">
        <p>
          You must be at least 18 years old to use Pairvio. By registering
          you confirm that you are 18 or older. We may suspend accounts we
          reasonably believe belong to minors.
        </p>
      </Section>
      <Section title="3. Not professional advice">
        <p>
          Pairvio is not a substitute for therapy, counseling, medical,
          or legal advice. AI-generated content is for skills practice only
          and may be inaccurate. You use it at your own judgment.
        </p>
      </Section>
      <Section title="4. Acceptable use">
        <p>You agree not to use the service to:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>generate sexual or explicit content;</li>
          <li>simulate, harass, or impersonate real people;</li>
          <li>seek advice for stalking, surveillance, manipulation, or coercion;</li>
          <li>attempt to bypass safety systems;</li>
          <li>violate any applicable law.</li>
        </ul>
        <p>We may suspend or terminate accounts that violate these rules.</p>
      </Section>
      <Section title="5. Your content">
        <p>
          You own the messages and journal entries you write. You grant us the
          limited license needed to process them through our AI providers in
          order to operate the service. We do not sell your conversations.
        </p>
      </Section>
      <Section title="6. Subscriptions">
        <p>
          Paid plans, if offered, renew automatically until cancelled. Pricing
          and features are described at checkout. Statutory refund rights are
          unaffected.
        </p>
      </Section>
      <Section title="7. Disclaimers and liability">
        <p>
          The service is provided "as is". To the maximum extent permitted by
          law, we are not liable for indirect or consequential damages arising
          from your use of the service or reliance on AI output.
        </p>
      </Section>
      <Section title="8. Changes">
        <p>
          We may update these terms; material changes will be announced in the
          app. Continued use after changes means acceptance.
        </p>
      </Section>
    </LegalLayout>
  );
}

export function PrivacyScreen() {
  return (
    <LegalLayout badge="Legal" title="Privacy Policy" updated={UPDATED}>
      <Section title="What we collect">
        <ul className="list-disc pl-5 space-y-1">
          <li>Account data: email, name, password (hashed).</li>
          <li>
            Practice data: your roleplay messages, feedback results, journal
            entries, and goals.
          </li>
          <li>Technical data: login timestamps, IP address, device platform.</li>
        </ul>
      </Section>
      <Section title="How we use it">
        <p>
          Before any AI sharing, Pairvio asks for your explicit permission. If
          you allow it, text you type or paste, conversation screenshots you
          select, situation and profile details you choose to provide, and—when using voice—live audio or an audio
          recording and its transcript are sent to OpenAI, L.L.C. to generate
          screenshot analysis, roleplay replies, feedback and summaries, perform safety moderation,
          transcribe speech, and create spoken responses. OpenAI API inputs and
          outputs are not used to train its models by default. OpenAI may retain
          API content in abuse-monitoring logs for up to 30 days unless longer
          retention is legally required. We do not sell personal data or use
          conversations for advertising.
        </p>
      </Section>
      <Section title="Sharing and service providers">
        <p>
          With your explicit permission, the AI content described above is
          shared with OpenAI, L.L.C. We also use infrastructure providers to
          host and secure Pairvio. We select and contractually require our
          service providers to protect personal data to the same or an
          equivalent standard described in this policy.
        </p>
      </Section>
      <Section title="Coach memory (personalization)">
        <p>
          To make practice sessions feel personal, we keep a small "coach
          profile" for your account: your quiz communication style, the skill
          areas you're working on, short AI-written notes about your
          communication habits, the answers you give in the "Get to know you"
          setup (relationship status, goal, your own words), and a one-line
          summary of your last session. If you explicitly mention a life change
          during a practice (e.g. a breakup), the AI may suggest updating your
          profile status — but nothing is saved unless you confirm it.
          Practice messages and voice transcripts are stored in your Pairvio
          session history; raw audio is not retained by Pairvio after
          processing. The AI is instructed not to record sensitive categories
          in coach memory (such as health or religion). You can
          view, edit, or erase this profile at any time from your Profile
          ("What your coach knows"), and it is deleted with your account.
        </p>
      </Section>
      <Section title="Safety processing">
        <p>
          Messages are automatically screened for safety (e.g. self-harm,
          sexual content involving minors, harassment). Flagged events are
          stored with a short excerpt for moderation review.
        </p>
      </Section>
      <Section title="Retention and deletion">
        <p>
          Your data is kept while your account is active. You can delete your
          account from Settings at any time; this permanently deletes your
          practice sessions, messages, journal, and goals. Backups are purged
          on a rolling schedule.
        </p>
      </Section>
      <Section title="Your rights">
        <p>
          You may revoke AI-sharing permission in the app under Settings &gt;
          AI data sharing permission. Pairvio will then ask again before
          sending new content to OpenAI. {" "}
          Depending on where you live (e.g. GDPR, CCPA), you may have rights to
          access, correct, export, or delete your data. Contact us at
          privacy@pairvio.app to exercise them.
        </p>
      </Section>
      <Section title="Children">
        <p>
          Pairvio is for adults 18+. We do not knowingly collect data from
          minors; suspected minor accounts are removed.
        </p>
      </Section>
    </LegalLayout>
  );
}
